import React, { useContext, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import apiRequest from '../ApiRequest';
import GlobalContext from '../GlobalContext';
import Button from '../Button';
import Modal from '../modal/Modal';
import '../../scss/MapDetails.scss';

export default function MapDetails({ setOpenSidebar }) {
  const { t } = useTranslation();
  const { token, setAlert, setShowLoader } = useContext(GlobalContext);
  const queryClient = useQueryClient();
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedNeighborhood, setSelectedNeighborhood] = useState('');
  const [address, setAddress] = useState('');
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [retrieveMessage, setRetrieveMessage] = useState('');

  const { data: citiesData, isLoading } = useQuery('getCourierCities', () =>
    apiRequest({
      command: 'GET_COURIER_CITIES',
      sessionId: token
    })
  );

  const { data: districtsData, isLoading: isLoadingDistricts } = useQuery(
    ['getCourierDistricts', selectedCity],
    () =>
      apiRequest(
        {
          command: 'GET_COURIER_DISTRICTS',
          sessionId: token
        },
        {
          cityId: selectedCity
        }
      ),
    {
      enabled: !!selectedCity
    }
  );

  const { data: neighborhoodsData, isLoading: isLoadingNeighborhoods } = useQuery(
    ['getCourierNeighborhoods', selectedDistrict],
    () =>
      apiRequest(
        {
          command: 'GET_COURIER_NEIGHBORHOODS',
          sessionId: token
        },
        {
          districtId: selectedDistrict
        }
      ),
    {
      enabled: !!selectedDistrict
    }
  );

  const { data: savedAddressData } = useQuery('getCustomerSavedAddress', () =>
    apiRequest({
      command: 'GET_CUSTOMER_SAVED_ADDRESS',
      sessionId: token
    })
  );

  const cities = citiesData?.data?.result || [];
  const districts = districtsData?.data?.result || [];
  const neighborhoods = neighborhoodsData?.data?.result || [];

  useEffect(() => {
    const savedAddress = savedAddressData?.data?.result;
    if (savedAddress) {
      if (savedAddress.city_id) {
        setSelectedCity(savedAddress.city_id.toString());
      }
      if (savedAddress.district_id) {
        setSelectedDistrict(savedAddress.district_id.toString());
      }
      if (savedAddress.neighborhood_id) {
        setSelectedNeighborhood(savedAddress.neighborhood_id.toString());
      }
      if (savedAddress.address) {
        setAddress(savedAddress.address);
      }
    }
  }, [savedAddressData]);

  const saveAddressMutation = useMutation(
    (params) =>
      apiRequest(
        {
          command: 'SAVE_GOOGLE_MAP_ADDRESS_V2',
          sessionId: token
        },
        {
          params: params
        }
      ),
    {
      onSuccess: (response) => {
        if (response.data.status === 'OK') {
          // call retrieve_courier CHECK
          fetch('https://www.usa2georgia.com/private_usa_ws/rest.php', {
            method: 'POST',
            body: JSON.stringify({
              command: 'retrieve_courier',
              secret: 'EFDCF20F32-B233EA-45DF75-B01E-8098A066',
              sessionId: token,
              action: 'CHECK'
            })
          }).then((response) => {
            response.json().then((res) => {
              if (res?.status === 'OK') {
                setRetrieveMessage(res?.result?.message);
                setOpenConfirmation(true);
              } else {
                setAlert(['error', res?.message.startsWith('_') ? t(res?.message) : res?.message]);
              }
              setShowLoader(false);
            });
          });
        } else {
          setAlert(['error', t(response.data.message)]);
          setShowLoader(false);
        }
      },
      onError: (response) => {
        setAlert(['error', t(response?.data?.message || '_ERROR')]);
        setShowLoader(false);
      }
    }
  );

  const retriveCourierAction = () => {
    fetch('https://www.usa2georgia.com/private_usa_ws/rest.php', {
      method: 'POST',
      body: JSON.stringify({
        command: 'retrieve_courier',
        secret: 'EFDCF20F32-B233EA-45DF75-B01E-8098A066',
        sessionId: token,
        action: 'RETRIEVE'
      })
    }).then((response) => {
      response.json().then((res) => {
        if (res?.status === 'OK') {
          setOpenConfirmation(false);
          setOpenSidebar(false);
          queryClient.invalidateQueries('getArrived');
          setAlert(['success', res?.message.startsWith('_') ? t(res?.message) : res?.message]);
        } else {
          setAlert(['error', res?.message.startsWith('_') ? t(res?.message) : res?.message]);
        }
      });
    });
  };

  const handleCityChange = (e) => {
    setSelectedCity(e.target.value);
    setSelectedDistrict('');
    setSelectedNeighborhood('');
  };

  const handleDistrictChange = (e) => {
    setSelectedDistrict(e.target.value);
    setSelectedNeighborhood('');
  };

  const handleOrderCourier = () => {
    if (!address || address.trim().length === 0) {
      setAlert(['error', t('_PLEASE_ENTER_ADDRESS')]);
      return;
    }

    const selectedCityName = cities.find((c) => c.id.toString() === selectedCity)?.name || '';
    const selectedDistrictName = districts.find((d) => d.id.toString() === selectedDistrict)?.name || '';
    const selectedNeighborhoodName = neighborhoods.find((n) => n.id.toString() === selectedNeighborhood)?.name || '';

    const formattedAddressParts = [t('_' + selectedCityName), t(selectedDistrictName), t(selectedNeighborhoodName), address].filter((part) => part);
    const formattedAddress = formattedAddressParts.join(', ');

    const params = {
      city_id: selectedCity,
      district_id: selectedDistrict || null,
      neighborhood_id: selectedNeighborhood || null,
      address: address,
      city: selectedCityName,
      formattedAddress: formattedAddress
    };

    setShowLoader(true);
    saveAddressMutation.mutate(params);
  };

  const selectStyle = {
    width: '100%',
    padding: '12px 40px 12px 15px',
    borderRadius: '12px',
    border: '1px solid #ddd',
    fontSize: '16px',
    backgroundColor: '#fff',
    color: '#000',
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    backgroundImage:
      "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e\")",
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    backgroundSize: '20px',
    transition: 'all 0.2s ease'
  };

  return (
    <div className='MapDetails sideBarChildComponent'>
      <h3 className='SectionTitle sidebarTitle'>{t('_key_currier_service')}</h3>
      <div className='sidebar-child-scroll'>
        {cities.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>{t('_SELECT_CITY')}:</label>
            <select
              value={selectedCity}
              onChange={handleCityChange}
              style={selectStyle}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#f68424';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#ddd';
              }}
              disabled={isLoading}
            >
              <option value=''>{isLoading ? t('_LOADING') + '...' : t('_SELECT_CITY')}</option>
              {cities.map((city) => (
                <option key={city.id} value={city.id}>
                  {t('_' + city.name)}
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedCity && districts.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>{t('_SELECT_DISTRICT')}:</label>
            <select
              value={selectedDistrict}
              onChange={handleDistrictChange}
              style={selectStyle}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#f68424';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#ddd';
              }}
              disabled={isLoadingDistricts}
            >
              <option value=''>{isLoadingDistricts ? t('_LOADING') + '...' : t('_SELECT_DISTRICT')}</option>
              {districts.map((district) => (
                <option key={district.id} value={district.id}>
                  {t(district.name)}
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedDistrict && neighborhoods.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>{t('_SELECT_NEIGHBORHOOD')}:</label>
            <select
              value={selectedNeighborhood}
              onChange={(e) => setSelectedNeighborhood(e.target.value)}
              style={selectStyle}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#f68424';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#ddd';
              }}
              disabled={isLoadingNeighborhoods}
            >
              <option value=''>{isLoadingNeighborhoods ? t('_LOADING') + '...' : t('_SELECT_NEIGHBORHOOD')}</option>
              {neighborhoods.map((neighborhood) => (
                <option key={neighborhood.id} value={neighborhood.id}>
                  {t(neighborhood.name)}
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedCity && !isLoadingDistricts && !isLoadingNeighborhoods && (districts.length === 0 || (selectedDistrict && (neighborhoods.length === 0 || selectedNeighborhood))) && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>{t('_ADDRESS')}:</label>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              rows={5}
              placeholder={t('_ENTER_ADDRESS')}
              style={{
                width: '100%',
                padding: '12px 15px',
                borderRadius: '12px',
                border: '1px solid #ddd',
                fontSize: '16px',
                resize: 'vertical',
                fontFamily: 'inherit',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#f68424';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#ddd';
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#f68424';
                e.target.style.outline = 'none';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#ddd';
              }}
            />
          </div>
        )}
      </div>
      <div className='sidebarButtonDiv'>
        <Button handleClick={handleOrderCourier} text={t('_ORDER_COURIER')} btnStyle='button-primary d-flex justify-content-center' isLoading={saveAddressMutation.isLoading} />
      </div>
      <Modal showModal={openConfirmation} setShowModal={setOpenConfirmation}>
        <div className='d-flex flex-column'>
          <div className='modalText' style={{ height: '390px', overflow: 'auto' }}>
            {retrieveMessage?.includes('<') && retrieveMessage?.includes('>') ? <div dangerouslySetInnerHTML={{ __html: retrieveMessage }} /> : t(retrieveMessage)}
          </div>
          <div className='d-flex align-items-center justify-content-center modal-btn-div mt-4'>
            <Button handleClick={retriveCourierAction} type='button' btnStyle='bg-orange me-4 fill-balance-btn' text={t('_YES')} color='color-white' />
            <Button
              handleClick={() => {
                setOpenConfirmation(false);
              }}
              type='button'
              btnStyle='bg-orange fill-balance-btn secondary-btn-bg'
              text={t('_NO')}
              color='color-white'
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
